import { NgZone } from '@angular/core';

export class AndroidPaymentSDK {
  // Shared across ALL instances: window.androidPaymentCallback is a single global, and more
  // than one AndroidPaymentSDK can exist (each construction overwrites the global handler).
  // With per-instance state, a result delivered to the newest instance could never find a
  // callback registered on an older one — the promise would silently never settle.
  private static callbacks: { [id: string]: { resolve: (data: any) => void, reject: (err: any) => void } } = {};
  private static callbackId = 0;

  constructor(private zone?: NgZone) {
    console.log('[AndroidPaymentSDK] instance constructed — pending ids: ['
      + Object.keys(AndroidPaymentSDK.callbacks).join(', ') + ']');

    // Setup the callback handler
    (<any>window).androidPaymentCallback = (response) => {
      // The native side invokes this via webView.evaluateJavascript, which runs outside
      // Angular's zone — resolve inside the zone or change detection never runs for
      // anything awaiting these promises (dialogs, toasts, bindings).
      const dispatch = () => {
        // Native Android bridge objects don't serialize — parse if string, otherwise copy fields
        let parsed = response;
        if (typeof response === 'string') {
          try { parsed = JSON.parse(response); } catch (e) { parsed = { raw: response }; }
        }

        const callback = AndroidPaymentSDK.callbacks[parsed.callbackId];
        if (callback) {
          if (parsed.success) {
            callback.resolve(parsed.data);
          } else {
            // Build a plain error string from whatever the native layer provides
            const errorDetail = parsed.error || parsed.errorMessage || parsed.message
              || parsed.data || parsed.reason || parsed.code
              || (typeof response === 'string' ? response : 'Unknown reader error');
            callback.reject(errorDetail);
          }
          delete AndroidPaymentSDK.callbacks[parsed.callbackId];
        } else {
          // A dropped result here means a payment may have completed with no one
          // listening (e.g. the page reloaded mid-payment) — surface it loudly.
          console.error('[AndroidPaymentSDK] Received result for unknown callbackId '
            + parsed.callbackId + ' — pending ids: [' + Object.keys(AndroidPaymentSDK.callbacks).join(', ')
            + '] response: ' + JSON.stringify(parsed));
        }
      };

      if (this.zone) {
        this.zone.run(dispatch);
      } else {
        dispatch();
      }
    };
  }

  private register(resolve: (data: any) => void, reject: (err: any) => void): number {
    const id = AndroidPaymentSDK.callbackId++;
    AndroidPaymentSDK.callbacks[id] = { resolve, reject };
    return id;
  }

  discoverReaders() {
    return new Promise((resolve, reject) => {
      const id = this.register(resolve, reject);
      (<any>window).AndroidPayment.discoverReaders(id.toString());
    });
  }

  scanQRCode() {
    return new Promise((resolve, reject) => {
      const id = this.register(resolve, reject);
      (<any>window).AndroidPayment.scanQRCode(id.toString());
    });
  }
  showSettings() {
      (<any>window).AndroidPayment.showSettings();
  }

  disconnectReader() {
    return new Promise((resolve, reject) => {
      const id = this.register(resolve, reject);
      (<any>window).AndroidPayment.disconnectReader(id.toString());
    });
  }

  connectReader(serialNumber) {
    return new Promise((resolve, reject) => {
      const id = this.register(resolve, reject);
      (<any>window).AndroidPayment.connectReader(serialNumber, id.toString());
    });
  }

  collectPayment(amountCents, currency = 'usd') {
    return new Promise((resolve, reject) => {
      const id = this.register(resolve, reject);
      (<any>window).AndroidPayment.collectPayment(amountCents, currency, id.toString());
    });
  }

  // True when running inside the reader's WebView where the native bridge is injected.
  get isAndroid(): boolean {
    return typeof (<any>window).AndroidPayment !== 'undefined';
  }

  /**
   * Get the device battery level and charging status
   * @returns {Promise<{level: number, isCharging: boolean}>}
   */
  getBatteryLevel(): Promise<{ level: number, isCharging: boolean }> {
    if (!this.isAndroid) {
      return Promise.reject('Not running in Android environment');
    }

    return new Promise((resolve, reject) => {
      const id = this.register(resolve, reject);
      (<any>window).AndroidPayment.getBatteryLevel(id.toString());
    });
  }
}
